import { useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';

/**
 * DailyActivityChart — last N days activity across three series.
 * Accepts raw arrays; groups by day client-side.
 */
export default function DailyActivityChart({
  tickets = [],
  submissions = [],
  emails = [],
  days = 14,
}) {
  const data = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const buckets = [];
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      buckets.push({
        key,
        label: `${d.getMonth() + 1}/${d.getDate()}`,
        tickets: 0,
        submissions: 0,
        emails: 0,
      });
    }

    const tally = (arr, field, dateField = 'created_at') => {
      for (const r of arr) {
        const raw = r?.[dateField];
        if (!raw) continue;
        const key = new Date(raw).toISOString().slice(0, 10);
        const bucket = buckets.find((b) => b.key === key);
        if (bucket) bucket[field] += 1;
      }
    };

    tally(tickets, 'tickets');
    tally(submissions, 'submissions');
    tally(emails, 'emails', 'created_at');

    return buckets;
  }, [tickets, submissions, emails, days]);

  const totalAll = data.reduce((acc, d) => acc + d.tickets + d.submissions + d.emails, 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="border border-[#1a1a1a] bg-[#0d0d0d] p-5"
      data-testid="daily-activity-chart"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[10px] font-mono text-[#90E0EF] tracking-[0.2em] uppercase">
            SCANNER /// {days}D
          </p>
          <h3 className="font-outfit text-lg font-semibold text-white mt-1">
            Daily Gallery Activity
          </h3>
        </div>
        <div className="text-right">
          <p className="text-[9px] font-mono text-[#555] tracking-wider uppercase">Total Events</p>
          <p className="font-outfit text-xl font-semibold text-[#90E0EF] tabular-nums">
            {totalAll}
          </p>
        </div>
      </div>

      {totalAll === 0 ? (
        <div className="h-48 flex items-center justify-center">
          <p className="text-xs font-mono text-[#444]">
            // no activity recorded in last {days} days
          </p>
        </div>
      ) : (
        <div className="h-48 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 6, right: 10, bottom: 0, left: -20 }}>
              <defs>
                <linearGradient id="cyanLine" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#90E0EF" stopOpacity={0.3} />
                  <stop offset="50%" stopColor="#90E0EF" stopOpacity={1} />
                  <stop offset="100%" stopColor="#90E0EF" stopOpacity={0.3} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#1a1a1a" strokeDasharray="2 4" vertical={false} />
              <XAxis
                dataKey="label"
                stroke="#444"
                tick={{ fontSize: 9, fill: '#666', fontFamily: 'JetBrains Mono, monospace' }}
                tickLine={false}
                axisLine={{ stroke: '#222' }}
              />
              <YAxis
                stroke="#444"
                tick={{ fontSize: 9, fill: '#666', fontFamily: 'JetBrains Mono, monospace' }}
                tickLine={false}
                axisLine={{ stroke: '#222' }}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  background: '#0a0a0a',
                  border: '1px solid #222',
                  borderRadius: 0,
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: 11,
                }}
                labelStyle={{ color: '#90E0EF', fontSize: 10 }}
                itemStyle={{ color: '#ccc' }}
              />
              <Legend
                wrapperStyle={{
                  fontSize: 10,
                  fontFamily: 'JetBrains Mono, monospace',
                  paddingTop: 4,
                  color: '#666',
                }}
                iconType="plainline"
              />
              <Line
                type="monotone"
                dataKey="tickets"
                stroke="url(#cyanLine)"
                strokeWidth={1.8}
                dot={{ r: 2, fill: '#90E0EF', stroke: 'none' }}
                activeDot={{ r: 4, fill: '#90E0EF' }}
                isAnimationActive
                animationDuration={900}
              />
              <Line
                type="monotone"
                dataKey="submissions"
                stroke="#ffffff"
                strokeWidth={1.2}
                strokeDasharray="3 3"
                dot={{ r: 2, fill: '#ffffff', stroke: 'none' }}
                activeDot={{ r: 4, fill: '#ffffff' }}
                isAnimationActive
                animationDuration={900}
                animationBegin={150}
              />
              <Line
                type="monotone"
                dataKey="emails"
                stroke="#6c7a7d"
                strokeWidth={1.2}
                dot={{ r: 2, fill: '#6c7a7d', stroke: 'none' }}
                activeDot={{ r: 4, fill: '#6c7a7d' }}
                isAnimationActive
                animationDuration={900}
                animationBegin={300}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </motion.div>
  );
}
